import "./EditMemberEmailModal.scss";
import { useEffect, useState } from "react";
import { Modal, Button } from "antd";
import AccountMembersApi from "../../../api/AccountMembersApi";
import { success } from "../../../service/MessageService";
import { MemberDto, UpdateMemberDto } from "../../../generated/dashboard-client";

export type MemberFieldType = "no" | "email" | "password" | "name";

export interface EditMemberPasswordModalProps {
  visible: boolean;
  value: string;
  type: MemberFieldType;
  member: MemberDto;
  onCancel: () => void;
  onDone: (type: MemberFieldType, newPassword: string) => void;
}

const passMatchError = "Passwords must match.";
const passLengthError = "Password must be 8 or more characters.";

export default function EditMemberPasswordModal({
  value,
  type,
  visible,
  member,
  onCancel,
  onDone,
}: EditMemberPasswordModalProps) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [passwordLengthError, setPasswordLengthError] = useState("");
  const [currentPasswordError, setCurrentPasswordError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setCurrentPassword("");
    setNewPassword("");
    setConfirmNewPassword("");
    setPasswordError("");
    setPasswordLengthError("");
    setCurrentPasswordError("");
    setLoading(false);
  }, [value, visible]);

  function onSave() {
    const dto = {} as UpdateMemberDto;
    dto.password = newPassword;
    dto.currentPassword = currentPassword;

    setLoading(true);
    if (newPassword.length < 8) {
      setLoading(false);
      setPasswordLengthError(passLengthError);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setLoading(false);
      setPasswordError(passMatchError);
      return;
    }

    AccountMembersApi.updateMember(member.id || "", dto)
      .then((resp) => {
        success("Successfully updated!");
        onDone(type, newPassword);
      })
      .catch((e) => {
        setLoading(false);
        if (e.response) {
          const msg = !!e.response ? e.response.data.message : e.response;
          if (msg.toLowerCase().includes("password is not correct")) {
            setPasswordError("");
            setCurrentPasswordError(msg);
          } else {
            setPasswordError(msg);
          }
        }
      });
  }

  return (
    <Modal
      className={"edit-member-modal clean one"}
      visible={visible}
      closable={false}
    >
      <header>
        <h2>Change Password</h2>
      </header>

      <label>Current Password</label>
      <input
        className={currentPasswordError ? "lg error" : "lg"}
        value={currentPassword}
        placeholder={"confirm your password"}
        type={"password"}
        onChange={(ev) => {
          setCurrentPassword(ev.target.value.trim());
          setCurrentPasswordError("");
        }}
      />
      {currentPasswordError && (
        <span className={"error"}>{currentPasswordError}</span>
      )}
      <label>New Password</label>
      <input
        className={passwordLengthError ? "lg error" : "lg"}
        value={newPassword}
        placeholder={"new password"}
        type={"password"}
        onChange={(ev) => {
          setNewPassword(ev.target.value.trim());
          setPasswordLengthError("");
        }}
      />
      {!passwordLengthError && (
        <span>
          Use 8 or more characters with a mix of letters, numbers and symbols (no \ or ").
        </span>
      )}
      {passwordLengthError && (
        <span className={"error"}>{passwordLengthError}</span>
      )}

      <label>Confirm New Password</label>
      <input
        className={passwordError ? "lg error" : "lg"}
        value={confirmNewPassword}
        placeholder={"confirm your new password"}
        type={"password"}
        onChange={(ev) => {
          setConfirmNewPassword(ev.target.value.trim());
          setPasswordError("");
        }}
      />
      {passwordError && <span className={"error"}>{passwordError}</span>}

      <footer>
        <Button className={"cancel"} size={"large"} onClick={onCancel}>
          <span>Cancel</span>
        </Button>
        <Button
          className={"primary"}
          size={"large"}
          loading={loading}
          disabled={!currentPassword || !newPassword || !confirmNewPassword}
          onClick={onSave}
        >
          <span>Save</span>
        </Button>
      </footer>
    </Modal>
  );
}
